import _ from 'lodash'
import config from 'util/config.js'
import { getSelected, getPutterDesignation, getTranslucentPaintRules } from 'state/customize/selectors'

export default function getImageUrl(state, view) {
  if (!state || !state.putter) {return '';}

  let url = '';
  switch (view) {
    case 'FS':
      url = state.putter.faceSoleImage;
      break;
    case 'BC':
      url = state.putter.backCavityImage;
      break;
    case 'PP':
      url = state.putter.playerProfileImage;
      break;
    default:
      return '';
  }
  if (!url) { return ''; }

  const getParamsForState = _.partial(getParametersForProperty, state, view);
  const params = Object.keys(state.instance).map(getParamsForState).filter(_.identity).join('&');

  var sep = (url.indexOf("?") < 0) ? '?' : '&';
  return url + sep + params;
}

function objToParams(obj) {
  return _.toPairs(obj)
    .map(([k, v]) => k + '=' + v)
    .join('&');
}

function getParametersForProperty(state, view, key) {
  switch (key) {
    case 'finish':
      return finish(state);

    case 'initials':
      return initials(state);

    case 'paintFillLocations':
      return paintFillLocations(state)

    case 'flangeLine':
    case 'sightDot':
    case 'topLine':
      return lines(state, view, key);

    case 'stampSelections':
      return stamps(state, view);

    case 'topWeight':
    case 'weight':
      return weights(state, key)

    case 'topWeightEngraving':
    case 'weightEngraving':
    case 'weightEngravingHeel':
    case 'weightEngravingToe':
    case 'weightEngravingTopHeel':
    case 'weightEngravingTopToe':
      return ''; //handled by weights

    default:
      return '';
  }
}

function finish(state) { 
  const finish = getSelected(state, 'finish');
  if (!finish) { return ''; }
  return '$bodyColor=' + finish.colorCode; 
}

function initials(state) {
  const textTypeKeys = {
    InitialsFace: {
      type: '$faceTextType',
      char: '$faceText',
      period: '$faceTextP',
      archChar: '$archText',
      archPeriod: '$archTextP',
      color: '$faceTextColor',
    },
    InitialsBumperHeel: {
      type: '$heelTextType',
      char: '$heelText',
      period: '$heelTextP',
      archChar: '$heelArchText',
      archPeriod: '$heelArchTextP',
      color: '$heelTextColor',
    },
    InitialsBumperToe: {
      type: '$toeTextType',
      char: '$toeText',
      period: '$toeTextP',
      archChar: '$toeArchText',
      archPeriod: '$toeArchTextP',
      color: '$toeTextColor',
    }
  };

  const initials = getSelected(state, 'initials');

  if(_.isEmpty(initials))
    return '';

  // Build an array of objects, one object for each initials field Face/Heel/Toe with characters.
  const params = _.toPairs(initials).map(([initialsField, initialsSelections]) => {
    if (!initialsSelections.characters.length) return {};
    const initialsLoc = _.find(state.putter.initialsLocation, { label: initialsField });
    const maxLen = initialsLoc.maxCharacters || 0;

    const keyDict = textTypeKeys[initialsField];
    const styleType = (initialsSelections.personalizationStyle && initialsSelections.personalizationStyle.parameterKey) || 'straight';

    // Key names are unique on arched text selections
    const charKey = styleType !== 'arched' ? keyDict.char : keyDict.archChar;
    const periodKey = styleType !== 'arched' ? keyDict.period : keyDict.archPeriod;

    // "center" text within textfield character placeholders (#18399)
    // most textfields tend 'left' if input parity doesn't match field parity.
    // heels tend right to offset bumper text toward center 
    const roundFunc = (keyDict.type.indexOf("heel") > -1 ? Math.ceil : Math.floor);
    const offset = maxLen === 0 ? 0 : roundFunc((maxLen - initialsSelections.characters.length)/2);

    // array of individual characters and optional periods' param objects
    const chars = initialsSelections.characters.split('').map((c, i) => {
      return Object.assign(
        { [charKey + (offset + i + 1)]: encodeURIComponent(c) },
        (initialsSelections.separateWithPeriods) ? { [periodKey + (offset + i + 1)]: '.' } : {}
      );
    });

    return Object.assign(
      { [keyDict.type]: styleType }, //fieldName : straight|dancing|arched
      ...chars
    );
  })

  return objToParams(Object.assign(...params));
}

function makeOpaqueKeyName(paintKey){
  return (paintKey.indexOf('Color') > -1) ?
    paintKey.replace('Color', 'Opaque'):
    paintKey + 'Opaque'; 
}

function getColorCode(state, finish, location, color){
  //for weights with the 'raw' color, use the weight colorCode
  if(location && location.paintFillKey.toLowerCase().indexOf('weight') > -1 && color.isRaw)
  {
    if(location.paintFillKey.toLowerCase().indexOf('top') > -1)
    {
      const topWeight = getSelected(state, 'topWeight');
      if(topWeight)
        return topWeight.colorCode;
    }
    else
    {
      const weight = getSelected(state, 'weight');
      if(weight)
        return weight.colorCode;
    }
  }

  const translucentPaintRules = getTranslucentPaintRules(state, finish, location.parameterKey)

  return (translucentPaintRules.useBlackFinishColorCodes) ? //(finish && finish.useBlackFinishColorCodes 
          //&& location && location.paintFillKey.indexOf('weight') == -1
          //&& translucentPaintRules.allowTranslucent) ? //&& color && color.isTranslucent) ?
          color.colorCodeBlackFinish :
          color.colorCode; 
}

function paintFillLocations(state) {
  const allLocs = state.putter.paintFillLocation;
  const selected = getSelected(state, 'paintFillLocations');

  const paintfill = getSelected(state, 'paintFill');
  const unselected = (!paintfill || paintfill.id === 6) ? 
    allLocs.filter(l => !_.find(selected, s => s.location.parameterKey === l.parameterKey)) :
    [];

  const engravingOption = getSelected(state, 'weightEngraving');

  const hasTranslucentWeightEngraving = isTranslucentEngraving(engravingOption);

  const finish = getSelected(state, 'finish'); 

  let translucentEngravingParams = getSelected(state, 'paintFillLocations').filter(({color, location}) => !isWeightStampColorLocation(location));

  const paintLocs = hasTranslucentWeightEngraving ? translucentEngravingParams : selected;

  const params = paintLocs.map(({color, location}) => (
    { 
      ['$' + location.parameterKey]: getColorCode(state, finish, location, color) ,
      ['$' + makeOpaqueKeyName(location.parameterKey)]: (color.isTranslucent) ? 0 : 1
    }
  )).concat(
    unselected.map(s =>({
        ['$' + s.parameterKey]: "D6D6D6",
        ['$' + makeOpaqueKeyName(s.parameterKey)]: 1 
      }
    )) 
  )



  return params.length ? objToParams(Object.assign(...params)) : '';
}

function isWeightStampColorLocation(location)
{
    return (location.parameterKey == 'weightFrontColor' || location.parameterKey == 'weightRearColor' || location.parameterKey == 'topWeightFrontColor' || location.parameterKey == 'topWeightRearColor');
}

function stamps(state, view) {
  const selected = getSelected(state, 'stampSelections');
  if (!selected.length) { return '' };

  const paints = getSelected(state, 'paintFillLocations');
  const params = selected.map(({ stamp, location }) =>{
    if (stamp){ 
      const paintLoc = _.find(paints, (p) => p.location.parameterKey === location.paintFillKey);
      const isTranslucent = (paintLoc && paintLoc.color) ? paintLoc.color.isTranslucent : null;
	  let stampImage = stamp.image;
	  
	  //use face image for toeStamp if it's set
	  if(location.parameterKey == "toeStamp" && stamp.faceToeImage !== undefined && stamp.faceToeImage.length > 0) {
		stampImage =  stamp.faceToeImage;
	  }
	  
      const img = (isTranslucent) ? stampImage + '_tr' : stampImage;
      if(paintLoc && paintLoc.color && location.isStampOnPaintFill)
      {
        return { 
          ['$' + location.parameterKey]: img, 
          ['$' + location.parameterKey + 'StampImageParams'] : getAdditionalEngravingParams(paintLoc.color, 'img', false),
          ['$' + location.parameterKey + 'StampDecalParams'] : getAdditionalEngravingParams(paintLoc.color, 'decal', false).replace('__COLOR__', location.paintFillKey),
        }
      }
      else
      {
        return { ['$' + location.parameterKey]: img }
      }
    }
    return {}
  });
  return objToParams(Object.assign(...params));
}


// These are the old hardcoded exceptions for putters that don't meet the naming convention 
let lineOverrides = {
  '2014_futurax5': {
    'BC': {
      'top_line': '2014_futurax5_BC3_top_line',
      'sight_dot': '2014_futurax5_BC3_sight_dot',
    },
    'PP':{
      'top_line': '2014_futurax5_pp_2_top_line',
      'sight_dot': '2014_futurax5_pp_2_sight_dot',
    },
  },
  '2015_Golo_5': {
    'BC': {
      'top_line': '2015_Golo_5_BP_top_line',
      'sight_dot': '2015_Golo_5_BP_sight_dot',
    },
    'PP':{
      'top_line': '2015_Golo_5_PP_top_line',
      'sight_dot': '2015_Golo_5_PP_sight_dot',
    } 
  },
  '2012_Circa_62_No3': {
    'BC': {
      'top_line': '2012_Circa_62_No3_BC_sight_dot',
    },
    'PP':{
      'top_line': '2012_Circa_62_No3_PP_sight_dot',
    } 
  } 
}
function getLineImage(putterDesignation, view, lineType){
  return _.get(lineOverrides,`${putterDesignation}.${view}.${lineType}`) || `${putterDesignation}_${view}_${lineType}`;
}


function lines(state, view, fieldKey){ 
  // selected params
  const lines = getSelected(state, fieldKey); 
  let paramKeys = (lines && lines.parameterKeys.split(',')) || [];
  const putterDesignation = getPutterDesignation(state);

  // existing params on stock putter
  if (fieldKey !== 'sightDot' &&
    //exceptions with baked-in perpendicular lines that don't want new line images overlaid
    ['2014_futurax5','2015_Golo_5','2016_Futura_X7','2016_Futura_X7M',
    '2016_Futura_X7M_Dualbalance','2016_Futura_5CB','2016_Futura_5MB',
    '2016_Futura_5S','2016_Futura_5W','2016_Futura_6M','2016_Futura_6M_DualBalance',
    '2016_Futura_7M'].indexOf(putterDesignation) === -1
  ){
    const paramsKey = ((fieldKey === 'topLine') ? 'existingTopLineParameters' : 'existingFlangeLineParameters');
    const existingParams = state.putter[paramsKey];
    if (existingParams.length > 0) {
      paramKeys = [...paramKeys, ...existingParams];
    }
  }
  if (paramKeys.length === 0){return;}

  const paints = getSelected(state, 'paintFillLocations');

  const lineType = _.snakeCase(fieldKey);
  const value = getLineImage(putterDesignation, view, lineType);

  const params = paramKeys.map((k) =>{ 
    const paintFillKey = k + 'Color'
    const paintLoc = _.find(paints, (p) => p.location.parameterKey === paintFillKey);
    const isTranslucent = (paintLoc && paintLoc.color && paintLoc.color.isTranslucent) 
    const suffix = isTranslucent ? '_tr' : '';

    return { ['$' + k] : value + suffix } 
  })

  return objToParams(Object.assign(...params));
}



  // if(sandbox.putterOptions.get('putterModel') === 5) {
  //  faceSoleOptions.$weightFrontDefault = '2012_Studio_Select_Fastback_FS_front_weight';
  //  faceSoleOptions.$weightRearDefault = '2012_Studio_Select_Fastback_FS_rear_weight'; 
  // } 

function getAdditionalEngravingParams(paintfill, type, translucentOverride){
  const pre = (paintfill.isTranslucent || translucentOverride) ? 'translucent' : 'opaque';
  const key = (type === 'img') ? 'PaintEngravingImgParams' : 'PaintEngravingDecalParams';
  return encodeURIComponent(config[pre + key]); 
}

function isTranslucentEngraving(engravingOption) {
    return engravingOption != null && engravingOption.label.indexOf('Only') > -1;
}

function weights(state, fieldKey) { 
  const isTop = (fieldKey === 'topWeight');
  const keys = {
    weight: isTop ? 'topWeight' : 'weight',
    weightEngraving: isTop ? 'topWeightEngraving' : 'weightEngraving',
    weightEngravingHeel: isTop ? 'weightEngravingTopHeel' : 'weightEngravingHeel',
    weightEngravingToe: isTop ? 'weightEngravingTopToe' : 'weightEngravingToe',
    weightRearColor : isTop ? 'topWeightRearColor' : 'weightRearColor',
    weightFrontColor : isTop ? 'topWeightFrontColor' : 'weightFrontColor',

    $weightFrontNumRim: isTop ? '$topWeightFrontNumRim' : '$weightFrontNumRim',
    $weightRearNumRim: isTop ? '$topWeightRearNumRim' : '$weightRearNumRim',
    $weightRearStamp: isTop ? '$topWeightRearStamp' : '$weightRearStamp',
    $weightFrontStamp: isTop ? '$topWeightFrontStamp' : '$weightFrontStamp',
    $weightRimColor: isTop ? '$topWeightRimColor' : '$weightRimColor', 

    $weightFrontStampImageParams : isTop ? '$topWeightFrontStampImageParams' : '$weightFrontStampImageParams',
    $weightFrontStampDecalParams : isTop ? '$topWeightFrontStampDecalParams' : '$weightFrontStampDecalParams', 
    $weightFrontOpaque: isTop ? '$topWeightFrontOpaque' : '$weightFrontOpaque', 
    $weightRearStampImageParams : isTop ? '$topWeightRearStampImageParams' : '$weightRearStampImageParams',
    $weightRearStampDecalParams  : isTop ? '$topWeightRearStampDecalParams' : '$weightRearStampDecalParams',
    $weightRearOpaque: isTop ? '$topWeightRearOpaque' : '$weightRearOpaque', 
  }

  const weightSelection = getSelected(state, keys.weight); 
  const hasWeightEngraving = getSelected(state, keys.weightEngraving);
  const heelWeightEngraving = getSelected(state, keys.weightEngravingHeel)
  const toeWeightEngraving = getSelected(state, keys.weightEngravingToe)

  
  const engravingOption = getSelected(state, 'weightEngraving');  
  const hasTranslucentWeightEngraving = isTranslucentEngraving(engravingOption);
  
  let params = {
    [keys.$weightFrontNumRim]: hasWeightEngraving && weightSelection ? weightSelection.image : 'transparent',
    [keys.$weightRearNumRim]: hasWeightEngraving && weightSelection ? weightSelection.image : 'transparent',
  };
  
  var heelWeightEngravingImg = 'transparent';
  if(heelWeightEngraving && heelWeightEngraving.image) {
    heelWeightEngravingImg = heelWeightEngraving.image;
    if(hasTranslucentWeightEngraving) {
        heelWeightEngravingImg += '_tr';
    }
  }

  var toeWeightEngravingImg = 'transparent';
  if(toeWeightEngraving && toeWeightEngraving.image) {
    toeWeightEngravingImg = toeWeightEngraving.image;
    if(hasTranslucentWeightEngraving) {
        toeWeightEngravingImg += '_tr';
    }
  }

  if (hasWeightEngraving) { 
    params[keys.$weightRearStamp] = heelWeightEngravingImg;
    params[keys.$weightFrontStamp] = toeWeightEngravingImg; 
  } else if (weightSelection){ 
    params[keys.$weightRearStamp] = weightSelection.image;
    params[keys.$weightFrontStamp] = weightSelection.image;
  }

  const paints = getSelected(state, 'paintFillLocations');
  const rearPaintLoc = _.find(paints, (p) => p.location.parameterKey === keys.weightRearColor);
  const rearPaintFill = (rearPaintLoc && rearPaintLoc.color) || null;
  if (rearPaintFill){ 
      params[keys.$weightRearStampImageParams] = getAdditionalEngravingParams(rearPaintFill, 'img', hasTranslucentWeightEngraving);
    if(hasTranslucentWeightEngraving) 
        params[keys.$weightRearStampDecalParams] = encodeURIComponent("color=" + rearPaintLoc.color.colorCode);
    else
        params[keys.$weightRearStampDecalParams] = getAdditionalEngravingParams(rearPaintFill, 'decal', false).replace('__COLOR__', keys.weightRearColor);
    params[keys.$weightRearOpaque] = rearPaintFill.isTranslucent ? '0' : '1';
  }

  const frontPaintLoc = _.find(paints, (p) => p.location.parameterKey === keys.weightFrontColor);
  const frontPaintFill = (frontPaintLoc && frontPaintLoc.color) || null;
  if (frontPaintFill){ 
      params[keys.$weightFrontStampImageParams] = getAdditionalEngravingParams(frontPaintFill, 'img', hasTranslucentWeightEngraving);
    if(hasTranslucentWeightEngraving) 
        params[keys.$weightFrontStampDecalParams] = encodeURIComponent("color=" + frontPaintLoc.color.colorCode);
    else
        params[keys.$weightFrontStampDecalParams] = getAdditionalEngravingParams(frontPaintFill, 'decal', false).replace('__COLOR__', keys.weightFrontColor);
    params[keys.$weightFrontOpaque] = frontPaintFill.isTranslucent ? '0' : '1';
  }


  // This is the copper rim color; not the paintfill color. Those are painted as paintfillLocations.
  const weightColor = (weightSelection && weightSelection.colorCode) ||  null; 
  if (weightColor) { 
    params[keys.$weightRimColor] = weightColor;
  }
  return objToParams(params);
}
